import AddRounded from '@mui/icons-material/AddRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import InventoryRounded from '@mui/icons-material/InventoryRounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import MoveToInboxRounded from '@mui/icons-material/MoveToInboxRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import ShoppingBagRounded from '@mui/icons-material/ShoppingBagRounded';
import {
  Box,
  Button,
  Card,
  Chip,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, SearchField, type Column } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { EmptyState, PageHeader } from '../../components/states';
import { businessDate } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import {
  useGoodsReceipts,
  usePurchaseOrders,
  useSupplierInvoices,
  type GoodsReceiptSummary,
  type ListParams,
  type PurchaseOrderSummary,
  type SupplierInvoiceSummary,
} from './api';
import { BillStatusChip, ErrorState, OrderStatusChip, ReceiptStatusChip } from './components/ui';
import { useExportSelection } from '../dataio/useExportSelection';
import { filesColumn, useAttachmentCounts } from '../attachments';

type TabKey = 'orders' | 'receipts' | 'bills';

const STATUS_FILTERS: Record<TabKey, { value: string; label: string }[]> = {
  orders: [
    { value: '', label: 'All' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'CONFIRMED', label: 'Confirmed' },
    { value: 'PARTIALLY_RECEIVED', label: 'Partly received' },
    { value: 'RECEIVED', label: 'Received' },
    { value: 'CLOSED', label: 'Closed' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ],
  receipts: [
    { value: '', label: 'All' },
    { value: 'POSTED', label: 'Posted' },
    { value: 'REVERSED', label: 'Reversed' },
  ],
  bills: [
    { value: '', label: 'All' },
    { value: 'POSTED', label: 'Posted' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ],
};

/** List state lives in the URL so filters survive reloads and back navigation. */
function useListParams() {
  const [sp, setSp] = useSearchParams();
  const tab = (
    ['orders', 'receipts', 'bills'].includes(sp.get('tab') ?? '') ? sp.get('tab') : 'orders'
  ) as TabKey;
  const params: ListParams = {
    page: Number(sp.get('page') ?? 1) || 1,
    pageSize: Number(sp.get('pageSize') ?? 25) || 25,
    q: sp.get('q') ?? undefined,
    status: sp.get('status') ?? undefined,
    from: sp.get('from') ?? undefined,
    to: sp.get('to') ?? undefined,
  };
  const update = useCallback(
    (patch: Record<string, string | number | undefined>, resetPage = true) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === '') next.delete(k);
            else next.set(k, String(v));
          }
          if (resetPage && !('page' in patch)) next.delete('page');
          return next;
        },
        // Switching tabs is a step Back returns from; filter and page changes are not.
        { replace: !('tab' in patch) },
      );
    },
    [setSp],
  );
  return { tab, params, update };
}

export function PurchasesHubPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { tab, params, update } = useListParams();

  return (
    <Box>
      <PageHeader
        title="Purchases"
        subtitle="Order from suppliers, receive stock and record their bills."
        actions={
          <>
            {can('purchases.receive') && (
              <Button
                variant="outlined"
                startIcon={<MoveToInboxRounded />}
                onClick={() => navigate('/purchases/receipts/new')}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                Receive goods
              </Button>
            )}
            {can('purchases.bill') && (
              <Button
                variant="outlined"
                startIcon={<ReceiptLongRounded />}
                onClick={() => navigate('/purchases/bills/new')}
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
              >
                Record bill
              </Button>
            )}
            {can('purchases.create') && (
              <Button
                variant="contained"
                startIcon={<AddRounded />}
                onClick={() => navigate('/purchases/orders/new')}
              >
                New order
              </Button>
            )}
          </>
        }
      />

      <PipelineStrip onPick={(status) => update({ tab: 'orders', status, q: undefined })} />

      <Tabs
        value={tab}
        onChange={(_, v: TabKey) =>
          update({ tab: v === 'orders' ? undefined : v, status: undefined, q: undefined })
        }
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab
          value="orders"
          label="Purchase orders"
          icon={<ShoppingBagRounded />}
          iconPosition="start"
        />
        <Tab
          value="receipts"
          label="Goods receipts"
          icon={<InventoryRounded />}
          iconPosition="start"
        />
        <Tab
          value="bills"
          label="Supplier bills"
          icon={<DescriptionRounded />}
          iconPosition="start"
        />
      </Tabs>

      {tab === 'orders' && <OrdersTable params={params} update={update} />}
      {tab === 'receipts' && <ReceiptsTable params={params} update={update} />}
      {tab === 'bills' && <BillsTable params={params} update={update} />}
    </Box>
  );
}

// --- pipeline -------------------------------------------------------------------------------

function PipelineStrip({ onPick }: { onPick: (status: string) => void }) {
  const drafts = usePurchaseOrders({ status: 'DRAFT', pageSize: 1 });
  const confirmed = usePurchaseOrders({ status: 'CONFIRMED', pageSize: 1 });
  const partial = usePurchaseOrders({ status: 'PARTIALLY_RECEIVED', pageSize: 1 });
  const tiles: {
    status: string;
    label: string;
    hint: string;
    icon: ReactNode;
    tone: 'primary' | 'info' | 'warning';
    count: number | undefined;
  }[] = [
    {
      status: 'DRAFT',
      label: 'Drafts to confirm',
      hint: 'Not sent to suppliers yet',
      icon: <EditNoteRounded />,
      tone: 'primary',
      count: drafts.data?.page.total,
    },
    {
      status: 'CONFIRMED',
      label: 'Awaiting delivery',
      hint: 'Confirmed, nothing received',
      icon: <LocalShippingRounded />,
      tone: 'info',
      count: confirmed.data?.page.total,
    },
    {
      status: 'PARTIALLY_RECEIVED',
      label: 'Partly received',
      hint: 'Some items still to come',
      icon: <InventoryRounded />,
      tone: 'warning',
      count: partial.data?.page.total,
    },
  ];
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: { xs: 1, sm: 2 },
        mb: 3,
      }}
    >
      {tiles.map((t) => (
        <Card
          key={t.status}
          component="button"
          type="button"
          onClick={() => onPick(t.status)}
          sx={{
            p: { xs: 1.5, sm: 2.25 },
            textAlign: 'left',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            cursor: 'pointer',
            font: 'inherit',
            color: 'inherit',
            bgcolor: 'background.paper',
            display: 'flex',
            gap: { xs: 1, md: 2 },
            '&:hover': {
              boxShadow: '0 12px 24px -6px rgba(16,24,40,0.10)',
              borderColor: `${t.tone}.main`,
            },
            '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 2 },
          }}
        >
          <Box
            sx={(th) => {
              const c = (th.vars ?? th).palette[t.tone].main;
              return {
                width: 44,
                height: 44,
                flexShrink: 0,
                borderRadius: '12px',
                display: 'grid',
                placeItems: 'center',
                color: c,
                background: `linear-gradient(135deg, color-mix(in srgb, ${c} 18%, transparent), color-mix(in srgb, ${c} 6%, transparent))`,
              };
            }}
          >
            {t.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {t.label}
            </Typography>
            <Typography variant="h4" className="tabular" sx={{ lineHeight: 1.2 }}>
              {t.count === undefined ? <Skeleton width={32} /> : t.count}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              {t.hint}
            </Typography>
          </Box>
        </Card>
      ))}
    </Box>
  );
}

// --- shared toolbar ---------------------------------------------------------------------------

function Toolbar({
  tab,
  params,
  update,
  placeholder,
}: {
  tab: TabKey;
  params: ListParams;
  update: (patch: Record<string, string | number | undefined>) => void;
  placeholder: string;
}) {
  const onSearch = useCallback((q: string) => update({ q }), [update]);
  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { md: 'center' } }}
      >
        <SearchField value={params.q ?? ''} onChange={onSearch} placeholder={placeholder} />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <TextField
            type="date"
            label="From"
            value={params.from ?? ''}
            onChange={(e) => update({ from: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: { xs: '50%', md: 160 } }}
          />
          <TextField
            type="date"
            label="To"
            value={params.to ?? ''}
            onChange={(e) => update({ to: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: { xs: '50%', md: 160 } }}
          />
        </Stack>
      </Stack>
      <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
        {STATUS_FILTERS[tab].map((s) => {
          const active = (params.status ?? '') === s.value;
          return (
            <Chip
              key={s.value || 'all'}
              label={s.label}
              onClick={() => update({ status: s.value })}
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              sx={{ height: 30 }}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}

interface TableProps {
  params: ListParams;
  update: (patch: Record<string, string | number | undefined>, resetPage?: boolean) => void;
}

function Supplier({ name, phone }: { name: string; phone?: string | null }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
        {name}
      </Typography>
      {phone && (
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {phone}
        </Typography>
      )}
    </Box>
  );
}

function DocNumber({
  number,
  sub,
  party,
  chip,
}: {
  number: string;
  sub?: ReactNode;
  party?: string;
  /** Status chip under the number on phones, where the status column is hidden. */
  chip?: ReactNode;
}) {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 650, whiteSpace: 'nowrap' }}>
        {number}
      </Typography>
      {party && (
        <Typography
          variant="caption"
          component="div"
          noWrap
          sx={{ display: { sm: 'none' }, fontWeight: 600, maxWidth: 160 }}
        >
          {party}
        </Typography>
      )}
      {sub && (
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {sub}
        </Typography>
      )}
      {chip && <Box sx={{ display: { sm: 'none' }, mt: 0.75 }}>{chip}</Box>}
    </Box>
  );
}

function useFiltered(params: ListParams) {
  return !!(params.q || params.status || params.from || params.to);
}

function OrdersTable({ params, update }: TableProps) {
  const exportSelection = useExportSelection('purchase_order');
  const { format } = useShop();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = usePurchaseOrders(params);
  const filtered = useFiltered(params);
  const fileCounts = useAttachmentCounts(
    'PURCHASE_ORDER',
    (data?.data ?? []).map((r) => r.id),
  );
  const columns: Column<PurchaseOrderSummary>[] = [
    {
      key: 'number',
      header: 'Order',
      render: (r) => (
        <DocNumber
          party={r.contact.name}
          chip={<OrderStatusChip status={r.status} />}
          number={r.number}
          sub={
            r.reference
              ? `Ref ${r.reference}`
              : `${String(r.lineCount)} ${r.lineCount === 1 ? 'item' : 'items'}`
          }
        />
      ),
    },
    {
      key: 'supplier',
      header: 'Supplier',
      hideBelow: 'sm',
      render: (r) => <Supplier {...r.contact} />,
    },
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'sm',
      render: (r) => <OrderStatusChip status={r.status} />,
    },
    {
      key: 'date',
      header: 'Date',
      hideBelow: 'md',
      render: (r) => businessDate(r.docDate, format),
    },
    {
      key: 'expected',
      header: 'Expected',
      hideBelow: 'lg',
      render: (r) => (r.expectedDate ? businessDate(r.expectedDate, format) : '—'),
    },
    {
      key: 'location',
      header: 'Deliver to',
      hideBelow: 'lg',
      render: (r) => r.location.name,
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (r) => <Money value={r.total} strong />,
    },
  ];
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  return (
    <>
      <DataTable
        {...exportSelection.table}
        columns={[...columns, ...filesColumn<PurchaseOrderSummary>(fileCounts)]}
        rows={data?.data}
        rowKey={(r) => r.id}
        loading={isLoading}
        page={data?.page}
        onPageChange={(page) => update({ page }, false)}
        onPageSizeChange={(pageSize) => update({ pageSize })}
        onRowClick={(r) => navigate(`/purchases/orders/${r.id}`)}
        toolbar={
          <Toolbar
            tab="orders"
            params={params}
            update={update}
            placeholder="Order number or supplier"
          />
        }
        empty={
          filtered ? (
            <EmptyState
              icon={<ShoppingBagRounded />}
              title="No matching orders"
              body="Try another search, status or date range."
            />
          ) : (
            <EmptyState
              icon={<ShoppingBagRounded />}
              title="No purchase orders yet"
              body="Create an order to tell a supplier what you need. Stock only changes when goods arrive."
              action={
                can('purchases.create')
                  ? {
                      label: 'New purchase order',
                      onClick: () => void navigate('/purchases/orders/new'),
                    }
                  : undefined
              }
            />
          )
        }
      />
      {exportSelection.dialog}
    </>
  );
}

function ReceiptsTable({ params, update }: TableProps) {
  const exportSelection = useExportSelection('goods_receipt');
  const { format } = useShop();
  const { can } = useAuth();
  const navigate = useNavigate();
  const showCost = can('products.view_cost');
  const { data, isLoading, error, refetch } = useGoodsReceipts(params);
  const filtered = useFiltered(params);
  const fileCounts = useAttachmentCounts(
    'GOODS_RECEIPT',
    (data?.data ?? []).map((r) => r.id),
  );
  const columns: Column<GoodsReceiptSummary>[] = [
    {
      key: 'number',
      header: 'Receipt',
      render: (r) => (
        <DocNumber
          party={r.contact.name}
          chip={<ReceiptStatusChip status={r.status} />}
          number={r.number}
          sub={
            r.supplierRef
              ? `Delivery note ${r.supplierRef}`
              : `${String(r.lineCount)} ${r.lineCount === 1 ? 'item' : 'items'}`
          }
        />
      ),
    },
    {
      key: 'supplier',
      header: 'Supplier',
      hideBelow: 'sm',
      render: (r) => <Supplier {...r.contact} />,
    },
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'sm',
      render: (r) => <ReceiptStatusChip status={r.status} />,
    },
    {
      key: 'source',
      header: 'Source',
      hideBelow: 'lg',
      render: (r) => (
        <Typography variant="body2" color="text.secondary">
          {r.purchaseOrderId ? 'Purchase order' : 'Direct'}
        </Typography>
      ),
    },
    { key: 'location', header: 'Location', hideBelow: 'lg', render: (r) => r.location.name },
    {
      key: 'date',
      header: 'Date',
      hideBelow: 'md',
      render: (r) => businessDate(r.docDate, format),
    },
    ...(showCost
      ? [
          {
            key: 'cost',
            header: 'Stock value',
            align: 'right' as const,
            render: (r: GoodsReceiptSummary) => <Money value={r.totalCost} strong />,
          },
        ]
      : []),
  ];
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  return (
    <>
      <DataTable
        {...exportSelection.table}
        columns={[...columns, ...filesColumn<GoodsReceiptSummary>(fileCounts)]}
        rows={data?.data}
        rowKey={(r) => r.id}
        loading={isLoading}
        page={data?.page}
        onPageChange={(page) => update({ page }, false)}
        onPageSizeChange={(pageSize) => update({ pageSize })}
        onRowClick={(r) => navigate(`/purchases/receipts/${r.id}`)}
        toolbar={
          <Toolbar
            tab="receipts"
            params={params}
            update={update}
            placeholder="Receipt number or supplier"
          />
        }
        empty={
          filtered ? (
            <EmptyState
              icon={<InventoryRounded />}
              title="No matching receipts"
              body="Try another search, status or date range."
            />
          ) : (
            <EmptyState
              icon={<InventoryRounded />}
              title="Nothing received yet"
              body="When a delivery arrives, receive it against its purchase order — or directly if there was no order."
              action={
                can('purchases.receive')
                  ? {
                      label: 'Receive goods',
                      onClick: () => void navigate('/purchases/receipts/new'),
                    }
                  : undefined
              }
            />
          )
        }
      />
      {exportSelection.dialog}
    </>
  );
}

function BillsTable({ params, update }: TableProps) {
  const exportSelection = useExportSelection('supplier_bill');
  const { format } = useShop();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useSupplierInvoices(params);
  const filtered = useFiltered(params);
  const fileCounts = useAttachmentCounts(
    'SUPPLIER_BILL',
    (data?.data ?? []).map((r) => r.id),
  );
  const columns: Column<SupplierInvoiceSummary>[] = [
    {
      key: 'number',
      header: 'Bill',
      render: (r) => (
        <DocNumber
          party={r.contact.name}
          chip={
            <BillStatusChip status={r.status} paymentStatus={r.paymentStatus} overdue={r.overdue} />
          }
          number={r.number}
          sub={r.supplierInvoiceNumber ? `Supplier # ${r.supplierInvoiceNumber}` : undefined}
        />
      ),
    },
    {
      key: 'supplier',
      header: 'Supplier',
      hideBelow: 'sm',
      render: (r) => <Supplier {...r.contact} />,
    },
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'sm',
      render: (r) => (
        <BillStatusChip status={r.status} paymentStatus={r.paymentStatus} overdue={r.overdue} />
      ),
    },
    {
      key: 'date',
      header: 'Date',
      hideBelow: 'lg',
      render: (r) => businessDate(r.docDate, format),
    },
    {
      key: 'due',
      header: 'Due',
      hideBelow: 'md',
      render: (r) => (
        <Typography
          variant="body2"
          color={r.overdue ? 'error.main' : undefined}
          sx={{ fontWeight: r.overdue ? 600 : undefined }}
        >
          {businessDate(r.dueDate, format)}
        </Typography>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      hideBelow: 'sm',
      render: (r) => <Money value={r.total} />,
    },
    {
      key: 'outstanding',
      header: 'Balance due',
      align: 'right',
      render: (r) =>
        r.status === 'CANCELLED' ? (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        ) : (
          <Money value={r.outstanding} strong />
        ),
    },
  ];
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  return (
    <>
      <DataTable
        {...exportSelection.table}
        columns={[...columns, ...filesColumn<SupplierInvoiceSummary>(fileCounts)]}
        rows={data?.data}
        rowKey={(r) => r.id}
        loading={isLoading}
        page={data?.page}
        onPageChange={(page) => update({ page }, false)}
        onPageSizeChange={(pageSize) => update({ pageSize })}
        onRowClick={(r) => navigate(`/purchases/bills/${r.id}`)}
        toolbar={
          <Toolbar
            tab="bills"
            params={params}
            update={update}
            placeholder="Bill number or supplier"
          />
        }
        empty={
          filtered ? (
            <EmptyState
              icon={<DescriptionRounded />}
              title="No matching bills"
              body="Try another search, status or date range."
            />
          ) : (
            <EmptyState
              icon={<DescriptionRounded />}
              title="No supplier bills yet"
              body="Record a supplier’s invoice from what you received, or for expenses like freight."
              action={
                can('purchases.bill')
                  ? { label: 'Record a bill', onClick: () => void navigate('/purchases/bills/new') }
                  : undefined
              }
            />
          )
        }
      />
      {exportSelection.dialog}
    </>
  );
}
